/**
 * Walk-in / editable appointment form: /admin/appointments/new + /:id
 *
 * Staff-side counterpart to the public /book page. No OTP; the row is
 * created directly as `confirmed` with reference_source=walk_in. On edit
 * we render the same layout but every field stays fully editable.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CalendarCheck2, ChevronLeft, Loader2, Save, ClipboardCheck, MessageCircle, Wrench } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { shareAptCard } from '../../../lib/aptMessages';
import { QuickFill, PickupCard, NameWithSuggest } from '../../../components/admin/appointments/BookingHelpers';
import { PackagePicker } from '../../../components/admin/appointments/PackagePicker';
import { ProblemPicker } from '../../../components/admin/appointments/CommonProblems';
import { formatErr, inputCls } from '../../../components/admin/billing/shared';

const isoInDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

const fmtSlot = (s) => s || '';
// Pasted/autofilled numbers often carry +91 or a leading 0: keep the 10-digit mobile.
const cleanMobile = (v) => {
  let d = String(v || '').replace(/\D/g, '');
  if (d.length > 10 && d.startsWith('91')) d = d.slice(2);
  else if (d.length > 10 && d.startsWith('0')) d = d.slice(1);
  return d.slice(0, 10);
};


const empty = () => ({
  service_type: '',
  customer: { name: '', phone: '', email: '' },
  vehicle: { registration_number: '', make: '', model: '', year: '', km: '' },
  slot_date: isoInDays(0),
  slot_window: '',
  notes: '',
  status: 'confirmed',
  reference_source: 'walk_in',
  advance_amount: 0,
  advance_paid_status: 'na',
  assigned_staff_id: '',
  assigned_staff_name: '',
  packages: [],
  pickup: { required: false, address: '', time: '', staff_id: '', staff_name: '' },
});

const AppointmentForm = () => {
  const { authHeader } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const [form, setForm] = useState(() => {
    const qs = new URLSearchParams(window.location.search);
    return { ...empty(), ...(qs.get('date') ? { slot_date: qs.get('date') } : {}), ...(qs.get('slot') ? { slot_window: qs.get('slot') } : {}) };
  });
  const [meta, setMeta] = useState({ services: [], slot_windows: [], statuses: [], reference_sources: [], advance_statuses: [], weekly_ppf_cap: 4, ppf_capped_services: [] });
  const [skillMatch, setSkillMatch] = useState(null);
  const [slots, setSlots] = useState([]);
  const [dayInfo, setDayInfo] = useState({ closed: false, blocked_windows: [], weekly_ppf: null });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(false);
  const [err, setErr] = useState('');
  const [linkedInspectionId, setLinkedInspectionId] = useState('');
  const [linkedJobCard, setLinkedJobCard] = useState(null);
  // WhatsApp must open from a tap (iOS blocks it after a save), so we show a link instead.
  const location = useLocation();
  const [waPrompt, setWaPrompt] = useState(!!location.state?.waPrompt);
  // /new and /:id share one component instance, so pick up the flag on navigation too.
  useEffect(() => { if (location.state?.waPrompt) setWaPrompt(true); }, [location.state]);
  // Snapshot of the last-loaded status so we can detect a
  // pending → confirmed transition on save and auto-open WhatsApp.
  const [prevStatus, setPrevStatus] = useState('');
  const [refNo, setRefNo] = useState('');
  const [vehicleCatalog, setVehicleCatalog] = useState({ makes: [], models_by_make: {} });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const sendCard = () => shareAptCard({ ...form, id, ref_no: refNo }, authHeader);
  const setNested = (k, patch) => setForm((f) => ({ ...f, [k]: { ...f[k], ...patch } }));

  const loadMeta = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/appointments/meta`, { headers: authHeader() });
      setMeta(data || {});
    } catch (e) { /* non-fatal */ }
  }, [authHeader]);

  const loadOne = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/appointments/${id}`, { headers: authHeader() });
      setForm({
        service_type: data.service_type || '',
        customer: {
          name: '', email: '', phone: '',
          ...Object.fromEntries(Object.entries(data.customer || {})
            .map(([k, v]) => [k, v === null || v === undefined ? '' : v])),
          phone: cleanMobile(data.customer?.phone),
        },
        vehicle: {
          registration_number: '', make: '', model: '', year: '', km: '',
          ...Object.fromEntries(Object.entries(data.vehicle || {})
            .map(([k, v]) => [k, v === null || v === undefined ? '' : v])),
        },
        slot_date: data.slot_date || isoInDays(0),
        slot_window: data.slot_window || '',
        notes: data.notes || '',
        status: data.status || 'confirmed',
        reference_source: data.reference_source || 'walk_in',
        advance_amount: data.advance_amount || 0,
        advance_paid_status: data.advance_paid_status || 'na',
        assigned_staff_id: data.assigned_staff_id || '',
        assigned_staff_name: data.assigned_staff_name || '',
        packages: data.packages || [],
        pickup: { ...empty().pickup, ...(data.pickup || {}) },
      });
      setLinkedJobCard(data.linked_job_card_id ? { id: data.linked_job_card_id, number: data.linked_job_card_number } : null);
      setLinkedInspectionId(data.linked_inspection_id || '');
      setPrevStatus(data.status || '');
      setRefNo(data.ref_no || '');
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  }, [authHeader, id, isNew]);

  const loadSlots = useCallback(async (date, serviceType) => {
    if (!date) return;
    try {
      const params = new URLSearchParams({ date });
      if (serviceType) params.set('service_type', serviceType);
      const { data } = await axios.get(`${API}/public/appointments/slots?${params.toString()}`);
      setSlots(data.slots || []);
      setDayInfo({
        closed: !!data.closed,
        blocked_windows: data.blocked_windows || [],
        weekly_ppf: data.weekly_ppf || null,
      });
    } catch (e) { setSlots([]); setDayInfo({ closed: false, blocked_windows: [], weekly_ppf: null }); }
  }, []);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadOne(); }, [loadOne]);
  // Warn when the assignee isn't rated OK on the skills this service needs.
  useEffect(() => {
    let alive = true;
    if (!form.assigned_staff_id || !form.service_type) { setSkillMatch(null); return undefined; }
    axios.get(`${API}/skills/match`, {
      headers: authHeader(),
      params: { staff_id: form.assigned_staff_id, service_type: form.service_type },
    })
      .then(({ data }) => { if (alive) setSkillMatch(data); })
      .catch(() => { if (alive) setSkillMatch(null); });
    return () => { alive = false; };
  }, [form.assigned_staff_id, form.service_type, authHeader]);

  useEffect(() => { loadSlots(form.slot_date, form.service_type); }, [form.slot_date, form.service_type, loadSlots, id]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/appointments/vehicle-catalog`);
        setVehicleCatalog(data || { makes: [], models_by_make: {} });
      } catch (e) { /* silent */ }
    })();
  }, []);

  const save = async () => {
    setErr(''); setSaving(true);
    try {
      const payload = {
        service_type: form.service_type,
        customer: {
          name: (form.customer.name || '').trim(),
          phone: (form.customer.phone || '').replace(/\D/g, ''),
          email: (form.customer.email || '').trim(),
        },
        vehicle: {
          registration_number: (form.vehicle.registration_number || '').toUpperCase().trim(),
          make: (form.vehicle.make || '').trim(),
          model: (form.vehicle.model || '').trim(),
          year: form.vehicle.year ? Number(form.vehicle.year) : null,
          km: form.vehicle.km ? Number(form.vehicle.km) : null,
        },
        slot_date: form.slot_date,
        slot_window: form.slot_window,
        notes: (form.notes || '').trim(),
        advance_amount: Number(form.advance_amount || 0),
        advance_paid_status: form.advance_paid_status,
        assigned_staff_id: form.assigned_staff_id || '',
        assigned_staff_name: form.assigned_staff_name || '',
        packages: form.packages,
        pickup: form.pickup,
      };
      if (isNew) {
        payload.status = form.status;
        payload.reference_source = form.reference_source;
        const { data } = await axios.post(`${API}/appointments`, payload, { headers: authHeader() });
        // Fresh walk-in that landed as `confirmed` → nudge WhatsApp
        // right away so ops doesn't forget to notify the customer.
        navigate(`/admin/appointments/${data.id}`, { replace: true, state: { waPrompt: data.status === 'confirmed' } });
      } else {
        payload.status = form.status;
        payload.reference_source = form.reference_source;
        await axios.patch(`${API}/appointments/${id}`, payload, { headers: authHeader() });
        // Only fire on the pending → confirmed transition; repeated
        // saves of an already-confirmed booking shouldn't re-open WA.
        const transitioned = prevStatus !== 'confirmed' && form.status === 'confirmed';
        await loadOne();
        if (transitioned) setWaPrompt(true);
      }
    } catch (e) { setErr(formatErr(e)); }
    finally { setSaving(false); }
  };

  const convert = async () => {
    if (linkedInspectionId) { navigate(`/admin/inspections/${linkedInspectionId}`); return; }
    if (!window.confirm('Create a service inspection from this appointment?')) return;
    setConverting(true);
    try {
      const { data } = await axios.post(`${API}/appointments/${id}/convert-to-inspection`, {}, { headers: authHeader() });
      navigate(`/admin/inspections/${data.inspection_id}`);
    } catch (e) { setErr(formatErr(e)); setConverting(false); }
  };

  const toJobCard = async () => {
    if (linkedJobCard) { navigate(`/admin/job-cards/${linkedJobCard.id}`); return; }
    if (!window.confirm('Create a job card from this appointment?')) return;
    setConverting(true);
    try {
      const { data } = await axios.post(`${API}/appointments/${id}/convert-to-job-card`, {}, { headers: authHeader() });
      navigate(`/admin/job-cards/${data.job_card_id}`);
    } catch (e) { setErr(formatErr(e)); setConverting(false); }
  };

  const quickFill = (m) => setForm((f) => ({
    ...f,
    customer: { ...f.customer, name: m.name || f.customer.name, phone: cleanMobile(m.phone) || f.customer.phone, email: m.email || f.customer.email, ...(m.prefix ? { prefix: m.prefix } : {}) },
    // A different customer replaces the vehicle too, so the last one's car isn't left behind.
    vehicle: m.phone && m.phone !== f.customer.phone
      ? { ...empty().vehicle, ...m.vehicle }
      : { ...f.vehicle, ...Object.fromEntries(Object.entries(m.vehicle || {}).filter(([, v]) => v)) },
  }));

  if (loading) {
    return <div className="p-8 text-center text-slate-500"><Loader2 className="inline animate-spin mr-2" size={18} /> Loading…</div>;
  }

  const missing = [
    !form.service_type && 'service', !form.customer.name?.trim() && 'customer name',
    !/^\d{10}$/.test(form.customer.phone || '') && '10-digit mobile', !form.slot_date && 'date', !form.slot_window && 'slot',
  ].filter(Boolean);
  const canSubmit = missing.length === 0;
  const newCustomer = (v) => {
    if (v.registration_number) setNested('vehicle', { registration_number: v.registration_number.slice(0, 10) });
    else setNested('customer', v);
  };

  return (
    <div className="p-4 sm:p-6 space-y-4 bg-slate-50 min-h-full" data-testid="appointment-form">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/admin/appointments')}
            className="w-9 h-9 rounded-lg border border-slate-300 text-slate-600 hover:bg-white flex items-center justify-center"
            data-testid="apt-back"
          ><ChevronLeft size={16} /></button>
          <div className="w-10 h-10 rounded-xl bg-teal-100 text-teal-700 flex items-center justify-center">
            <CalendarCheck2 size={20} />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900">{isNew ? 'New appointment' : 'Edit appointment'}</h1>
            <p className="text-xs text-slate-500">
              {isNew ? 'Records a slot directly for a walk-in customer (skips OTP).' : `Reference: ${refNo || id}`}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!isNew && prevStatus === 'confirmed' && form.customer?.phone && (
            <button
              type="button"
              onClick={sendCard}
              className="inline-flex items-center gap-1 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold px-3 py-2 rounded-lg transition"
              data-testid="apt-send-card-btn"
              title="Send the booking card + confirmation on WhatsApp"
            ><MessageCircle size={14} /> Send confirmation</button>
          )}
          {!isNew && ['pending', 'confirmed'].includes(form.status) && (
            <button
              onClick={convert}
              disabled={converting}
              className="inline-flex items-center gap-1 bg-white border border-teal-300 text-teal-700 hover:bg-teal-50 text-sm font-semibold px-3 py-2 rounded-lg transition"
              data-testid="apt-convert-btn"
            >
              {converting ? <Loader2 size={14} className="animate-spin" /> : <ClipboardCheck size={14} />}
              {linkedInspectionId ? 'Open linked inspection' : 'Convert to inspection'}
            </button>
          )}
          {!isNew && (['pending', 'confirmed'].includes(form.status) || linkedJobCard) && (
            <button
              onClick={toJobCard}
              disabled={converting}
              className="inline-flex items-center gap-1 bg-white border border-slate-300 text-slate-800 hover:bg-slate-50 text-sm font-semibold px-3 py-2 rounded-lg transition"
              data-testid="apt-jobcard-btn"
            >
              <Wrench size={14} /> {linkedJobCard ? `Open ${linkedJobCard.number}` : 'Convert to job card'}
            </button>
          )}
          <button
            onClick={save}
            disabled={!canSubmit || saving}
            className="inline-flex items-center gap-1 bg-teal-600 hover:bg-teal-700 disabled:opacity-40 text-white text-sm font-semibold px-4 py-2 rounded-lg transition"
            data-testid="apt-save"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save
          </button>
        </div>
      </div>
      {!canSubmit && !saving && (
        <p className="-mt-2 text-xs font-medium text-amber-700" data-testid="apt-missing">To save, add: {missing.join(', ')}.</p>
      )}

      {err && <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-3 text-sm" data-testid="apt-err">{err}</div>}
      {waPrompt && form.customer?.phone && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="apt-wa-prompt">
          <span className="text-sm font-semibold text-emerald-900">Booking confirmed. Send the confirmation to the customer:</span>
          <button type="button" onClick={() => { setWaPrompt(false); sendCard(); }}
            className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold" style={{ color: '#fff' }} data-testid="apt-wa-prompt-send">
            <MessageCircle size={14} /> Send confirmation card
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left column — service + vehicle + slot */}
        <div className="lg:col-span-2 space-y-4">
          {/* Service */}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <h2 className="text-sm font-semibold text-slate-800 mb-3">Service</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {(meta.services || []).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => set('service_type', s)}
                  className={`text-xs font-semibold px-2 py-2 rounded-lg border transition ${
                    form.service_type === s
                      ? 'bg-teal-600 text-white border-teal-600'
                      : 'bg-white text-slate-700 border-slate-300 hover:border-teal-400 hover:bg-teal-50'
                  }`}
                  data-testid={`apt-service-${s.toLowerCase().replace(/\s+/g, '-')}`}
                >{s}</button>
              ))}
            </div>
          </div>

          {/* Customer */}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <h2 className="text-sm font-semibold text-slate-800 mb-3">Customer</h2>
            <QuickFill authHeader={authHeader} onPick={quickFill} onNew={newCustomer} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <NameWithSuggest authHeader={authHeader} className={inputCls} value={form.customer.name} onChange={(v) => setNested('customer', { name: v })} onPick={quickFill} />
              <input className={inputCls} placeholder="Mobile" type="tel" inputMode="numeric" autoComplete="off" value={form.customer.phone} onChange={(e) => setNested('customer', { phone: cleanMobile(e.target.value) })} data-testid="apt-phone" />
              <input className={`${inputCls} sm:col-span-2`} placeholder="Email (optional)" value={form.customer.email} onChange={(e) => setNested('customer', { email: e.target.value })} data-testid="apt-email" />
            </div>
          </div>

          {/* Vehicle */}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <h2 className="text-sm font-semibold text-slate-800 mb-3">Vehicle</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <input className={inputCls} placeholder="Reg. no" maxLength={10} value={form.vehicle.registration_number} onChange={(e) => setNested('vehicle', { registration_number: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10) })} data-testid="apt-reg" />
              <input
                className={inputCls}
                placeholder="Make"
                list="apt-make-list"
                autoComplete="off"
                value={form.vehicle.make}
                onChange={(e) => {
                  const nextMake = e.target.value;
                  setNested('vehicle', {
                    make: nextMake,
                    ...(nextMake !== form.vehicle.make ? { model: '' } : {}),
                  });
                }}
                data-testid="apt-make"
              />
              <datalist id="apt-make-list">
                {(vehicleCatalog.makes || []).map((m) => <option key={m} value={m} />)}
              </datalist>
              <input
                className={inputCls}
                placeholder="Model"
                list="apt-model-list"
                autoComplete="off"
                value={form.vehicle.model}
                onChange={(e) => setNested('vehicle', { model: e.target.value })}
                data-testid="apt-model"
              />
              <datalist id="apt-model-list">
                {((vehicleCatalog.models_by_make || {})[form.vehicle.make] || []).map((m) => (
                  <option key={m} value={m} />
                ))}
              </datalist>
              <input className={inputCls} placeholder="Year" inputMode="numeric" value={form.vehicle.year} onChange={(e) => setNested('vehicle', { year: e.target.value })} data-testid="apt-year" />
              <input className={`${inputCls} sm:col-span-2`} placeholder="KM driven (optional)" inputMode="numeric" value={form.vehicle.km} onChange={(e) => setNested('vehicle', { km: e.target.value })} data-testid="apt-km" />
            </div>
          </div>

          {/* Slot */}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-baseline justify-between mb-3 gap-2">
              <h2 className="text-sm font-semibold text-slate-800">Slot</h2>
              <div className="flex items-center gap-2 text-[10px] font-semibold text-slate-500" data-testid="apt-slot-legend">
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Available</span>
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> Filling</span>
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-500" /> Booked</span>
              </div>
            </div>
            {/* Weekly PPF banner (only for PPF-family services) */}
            {form.service_type && (meta.ppf_capped_services || []).includes(form.service_type) && dayInfo.weekly_ppf?.cap > 0 && (
              <div
                className={`mb-3 rounded-lg border px-3 py-2 text-[11px] font-semibold ${
                  dayInfo.weekly_ppf.remaining <= 0
                    ? 'bg-rose-50 border-rose-200 text-rose-700'
                    : dayInfo.weekly_ppf.remaining <= 1
                      ? 'bg-amber-50 border-amber-200 text-amber-800'
                      : 'bg-emerald-50 border-emerald-200 text-emerald-800'
                }`}
                data-testid="apt-ppf-cap-banner"
              >
                {dayInfo.weekly_ppf.remaining <= 0
                  ? `PPF cap hit — ${dayInfo.weekly_ppf.used}/${dayInfo.weekly_ppf.cap} used this week. Pick a date next week.`
                  : `${dayInfo.weekly_ppf.remaining} of ${dayInfo.weekly_ppf.cap} PPF slots left this week.`}
              </div>
            )}
            {dayInfo.closed && (
              <div className="mb-3 rounded-lg border bg-slate-50 border-slate-200 text-slate-600 px-3 py-2 text-[11px] font-semibold" data-testid="apt-closed-banner">
                The workshop is closed on this day (see Settings › Booking rules).
              </div>
            )}
            {!dayInfo.closed && (dayInfo.blocked_windows || []).length > 0 && (
              <div className="mb-3 rounded-lg border bg-slate-50 border-slate-200 text-slate-600 px-3 py-2 text-[11px] font-semibold" data-testid="apt-friday-block-banner">
                Held today: {(dayInfo.blocked_windows || []).join(', ')}.
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-start">
              <input type="date" className={inputCls} value={form.slot_date} onChange={(e) => set('slot_date', e.target.value)} data-testid="apt-slot-date" />
              <div className="sm:col-span-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {(slots.length ? slots : (meta.slot_windows || []).map((w) => ({ window: w, remaining: null, used: null }))).map((s) => {
                  const active = form.slot_window === s.window;
                  const knownCap = s.remaining !== null;
                  const full = knownCap && s.remaining <= 0 && !active;
                  const cap = knownCap ? s.used + s.remaining : null;
                  const filling = knownCap && !full && s.remaining <= Math.max(1, Math.floor((cap || 4) * 0.25));
                  const reason = s.unavailable_reason;
                  const tone = !knownCap
                    ? 'bg-white text-slate-700 border-slate-300 hover:border-teal-400 hover:bg-teal-50'
                    : full
                      ? 'bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed'
                      : filling
                        ? 'bg-amber-50 text-amber-900 border-amber-200 hover:border-amber-400'
                        : 'bg-emerald-50 text-emerald-900 border-emerald-200 hover:border-emerald-400';
                  const dot = full ? 'bg-slate-400' : filling ? 'bg-amber-500' : 'bg-emerald-500';
                  const captionForReason = () => {
                    if (reason === 'closed') return 'Closed';
                    if (reason === 'blocked') return 'Held';
                    if (reason === 'ppf_weekly_cap') return 'PPF weekly cap';
                    return full ? 'Fully booked' : `${s.used}/${cap} booked · ${s.remaining} left`;
                  };
                  return (
                    <button
                      key={s.window}
                      type="button"
                      disabled={full}
                      onClick={() => set('slot_window', s.window)}
                      className={`relative text-left px-3 py-2.5 rounded-lg border transition ${
                        active ? 'bg-teal-600 text-white border-teal-600 shadow-sm' : tone
                      }`}
                      data-testid={`apt-slot-${s.window}`}
                    >
                      <div className="flex items-center gap-2">
                        {knownCap && <span className={`w-2 h-2 rounded-full flex-shrink-0 ${active ? 'bg-white' : dot}`} />}
                        <span className="text-xs font-bold tabular-nums">{fmtSlot(s.window)}</span>
                      </div>
                      {knownCap && (
                        <div className={`text-[10px] font-semibold mt-0.5 tabular-nums ${active ? 'text-white/80' : 'text-slate-500'}`}>
                          {captionForReason()}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <PackagePicker authHeader={authHeader} value={form.packages} onChange={(v) => set('packages', v)} />

          {/* Notes */}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <h2 className="text-sm font-semibold text-slate-800 mb-2">Problem / remarks</h2>
            <ProblemPicker value={form.notes} onChange={(v) => set('notes', v)} />
            <textarea className={inputCls} rows={3} placeholder="What the customer reported, or anything the team should know" value={form.notes} onChange={(e) => set('notes', e.target.value)} data-testid="apt-notes" />
          </div>
        </div>

        {/* Right column — status + advance */}
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <h2 className="text-sm font-semibold text-slate-800 mb-3">Booking meta</h2>
            <div className="space-y-2">
              <label className="block text-xs font-semibold text-slate-500">Status</label>
              <select className={inputCls} value={form.status} onChange={(e) => set('status', e.target.value)} data-testid="apt-status">
                {(meta.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <label className="block text-xs font-semibold text-slate-500 mt-2">Source</label>
              <select className={inputCls} value={form.reference_source} onChange={(e) => set('reference_source', e.target.value)} data-testid="apt-source">
                {(meta.reference_sources || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <label className="block text-xs font-semibold text-slate-500 mt-2">
                Assigned to <span className="font-normal text-slate-400">(credits this job in Performance)</span>
              </label>
              <select
                className={inputCls}
                value={form.assigned_staff_id || ''}
                onChange={(e) => {
                  const opt = (meta.staff_options || []).find((o) => o.id === e.target.value);
                  setForm((f) => ({
                    ...f,
                    assigned_staff_id: e.target.value,
                    assigned_staff_name: opt?.name || '',
                  }));
                }}
                data-testid="apt-assigned-staff"
              >
                <option value="">— Unassigned —</option>
                {(meta.staff_options || []).map((o) => (
                  <option key={o.id} value={o.id}>{o.name}{o.role ? ` · ${o.role}` : ''}</option>
                ))}
              </select>
              {skillMatch && !skillMatch.no_mapping && !skillMatch.ok && (
                <div className="mt-1.5 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-2 text-[11px] text-amber-800" data-testid="skill-match-warning">
                  <span className="font-semibold">Skill check: </span>
                  {form.assigned_staff_name} isn&apos;t rated OK yet on{' '}
                  {[...skillMatch.missing, ...skillMatch.unrated].map((m) => m.name).join(', ')}
                  {skillMatch.assessed_on ? ` (assessed ${skillMatch.assessed_on})` : ' (never assessed)'}.
                  Consider pairing them with a trainer.
                </div>
              )}
              {skillMatch && skillMatch.ok && (
                <div className="mt-1.5 text-[11px] text-emerald-700" data-testid="skill-match-ok">
                  Skill check passed for {skillMatch.service_type}.
                </div>
              )}
            </div>
          </div>

          <PickupCard pickup={form.pickup} staff={meta.staff_options || []} onChange={(patch) => setNested('pickup', patch)} />

          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <h2 className="text-sm font-semibold text-slate-800 mb-1">Advance received</h2>
            <p className="text-[11px] text-slate-500 mb-3">Online payment goes live once the gateway is activated — until then log the amount + mark manually.</p>
            <div className="space-y-2">
              <input type="number" min={0} step={100} className={inputCls} placeholder="₹ 0" value={form.advance_amount || ''} onChange={(e) => set('advance_amount', e.target.value)} data-testid="apt-advance" />
              <label className="block text-xs font-semibold text-slate-500">Advance status</label>
              <select className={inputCls} value={form.advance_paid_status} onChange={(e) => set('advance_paid_status', e.target.value)} data-testid="apt-advance-status">
                {(meta.advance_statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentForm;
